/**
 * 소싱 API — need(소싱 니즈) + rfq(견적 요청) 전반.
 *
 * - RFQ 단건/일괄 생성, RFQ 큐(Sprint 3), 전역 RFQ 리스트
 * - 니즈 생성/삭제/태그, 그룹(묶음) 이동/병합/해제, 무한 스크롤 목록
 * - 매입처 지정(클러스터 집합 동기화), 가격 응답 저장
 */

import { API_BASE, authHeaders, getJson, postJson, patchJson, loadingFetch, parseResponse, httpError } from './http';
import { z } from 'zod';
import type { LeadCategory } from './lead-api';
import type { SupplierRow } from './partner-api';
import {
  NeedCreateResponseSchema, DealSourcingResponseSchema, NeedRfqsResponseSchema,
  RfqListGroupedResponseSchema, RfqBatchFromDealItemsResponseSchema,
  SourcingListResponseSchema, NeedPurchaseRequestResponseSchema,
  SourcingSuppliersResponseSchema, SyncClusterResponseSchema,
  RfqBulkResponseSchema, RfqFullResponseSchema,
  SALES_REQUEST_TAGS as SHARED_SALES_REQUEST_TAGS,
  SOURCING_MEMO_TAGS as SHARED_SOURCING_MEMO_TAGS,
  type SourcingRfqBubble, type SourcingNeedRow, type NeedCreateResponse, type DealSourcingResponse,
  type SourcingListNeed, type SourcingCursor, type SourcingListResponse,
  type NeedRfqRow, type NeedRfqBatch, type NeedRfqsResponse,
  type RfqGroupedItem, type RfqGroupedRow, type RfqListGroupedResponse,
  type RfqBatchFromDealItemsResultRow, type RfqBatchFromDealItemsSkipped,
  type RfqBatchFromDealItemsResponse,
  type SupplierPick, type SyncClusterResponse,
  type RfqBulkResponse, type RfqFullResponse,
  type NeedCreateInput as SharedNeedCreateInput,
} from './schemas';

// 타입 정본 = shared/관문 z.infer (schemas.ts) — 인라인 중복 정의 제거(2026-07-12).
// 모든 응답은 zod safeParse 경계 1회 통과 — 이후 신뢰.
export type {
  SourcingRfqBubble, SourcingNeedRow, DealSourcingResponse,
  SourcingListNeed, SourcingCursor, SourcingListResponse,
  NeedRfqRow, NeedRfqBatch, NeedRfqsResponse,
  RfqGroupedItem, RfqGroupedRow, RfqListGroupedResponse,
  RfqBatchFromDealItemsResultRow, RfqBatchFromDealItemsSkipped, RfqBatchFromDealItemsResponse,
  SupplierPick, SyncClusterResponse, RfqBulkResponse, RfqFullResponse,
};

// ─── RFQ 전용 ────────────────────────────────────────────────────────────────

export interface RfqInput {
  rfq_date: string;
  supplier_id: number | string;
  option_part: string;
  requested_qty: number | string;
  inquiry_source?: string;
  notes?: string;
}

export interface QuoteInput {
  supplier_id: number | string;
  option_part: string;
  condition: string;
  price_usd: number | string;
  quoted_at: string;
  price_currency_original?: string;
  price_original_amount?: number | string;
  qty_available?: number | string | null;
  lead_time_days?: number | string | null;
  shipping_terms?: string;
  warranty_period?: string;
  payment_fee_pct?: number | string | null;
  vendor_trust_meta?: string;
  included_items_note?: string;
  offer_status?: string;
  quote_url?: string;
  notes?: string;
  expires_at?: string | null;
  is_active?: boolean;
}

/** POST /api/admin/rfq/bulk — RFQ + N supplier_quote 트랜잭션 생성 */
export async function createRfqBulk(body: {
  rfq: RfqInput;
  quotes: QuoteInput[];
}): Promise<RfqBulkResponse> {
  return postJson<RfqBulkResponse>('/api/admin/rfq/bulk', body, RfqBulkResponseSchema);
}

/** GET /api/admin/rfq/:id/full — 상세 페이지용 RFQ + 거래처 + 견적 행 전체 */
export async function fetchRfqFull(id: number | string): Promise<RfqFullResponse> {
  return getJson<RfqFullResponse>(`/api/admin/rfq/${encodeURIComponent(String(id))}/full`, { schema: RfqFullResponseSchema });
}

/** POST /api/admin/rfq/:id/purchase-request — 특정 거래처 구매요청 토글 (마이그140) */
export async function setRfqPurchaseRequest(id: string | number, requested: boolean): Promise<void> {
  await postJson<unknown>(`/api/admin/rfq/${encodeURIComponent(String(id))}/purchase-request`, { requested });
}

export type RfqAttachmentDTO = { key: string; url: string; mediaType: string; size_bytes: number; origName: string | null };

/** PUT /api/admin/rfq/need/:needId/visibility — 니즈별 매입처 표시/숨김 (마이그142) */
export async function setNeedVisibility(needId: string | number, visibleSupplierIds: number[]): Promise<void> {
  const res = await fetch(`${API_BASE}/api/admin/rfq/need/${encodeURIComponent(String(needId))}/visibility`, {
    method:  'PUT',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body:    JSON.stringify({ visible_supplier_ids: visibleSupplierIds }),
  });
  if (!res.ok) throw new Error(`표시 설정 저장 실패 (HTTP ${res.status})`);
}

/** POST /api/admin/rfq/:id/image — 거래처 수신 이미지/PDF 첨부(multipart, 복수). 갱신된 attachments 반환 (마이그141) */
export async function uploadRfqImages(id: string | number, files: File[]): Promise<RfqAttachmentDTO[]> {
  const form = new FormData();
  for (const f of files) form.append('files', f, f.name);
  const res = await fetch(`${API_BASE}/api/admin/rfq/${encodeURIComponent(String(id))}/image`, {
    method:  'POST',
    headers: authHeaders(),   // multipart: Content-Type 는 브라우저가 boundary 자동
    body:    form,
  });
  if (!res.ok) throw new Error(`첨부 업로드 실패 (HTTP ${res.status})`);
  const json = await res.json() as { rfq?: { attachments?: RfqAttachmentDTO[] } };
  return json.rfq?.attachments ?? [];
}

/** DELETE /api/admin/rfq/:id/image?key=... — 첨부 삭제. 갱신된 attachments 반환 (마이그141) */
export async function deleteRfqImage(id: string | number, key: string): Promise<RfqAttachmentDTO[]> {
  const res = await fetch(
    `${API_BASE}/api/admin/rfq/${encodeURIComponent(String(id))}/image?key=${encodeURIComponent(key)}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  if (!res.ok) throw new Error(`첨부 삭제 실패 (HTTP ${res.status})`);
  const json = await res.json() as { rfq?: { attachments?: RfqAttachmentDTO[] } };
  return json.rfq?.attachments ?? [];
}

// ─── RFQ 큐 (Sprint 3) ───────────────────────────────────────────────────────

export type RfqQueueStatus = 'pending' | 'sent' | 'quoted';

/** /api/admin/rfq/queue 의 items[i] 한 행 */
export interface RfqQueueItem {
  lead_item_id: number;
  lead_id: number;
  category: LeadCategory | null;
  option_part: string | null;
  item_description: string | null;
  quantity: number | null;
  needs_rfq: boolean;
  item_created_at: string;
  customer_id: number;
  customer_name: string | null;
  /** 최신 rfq 1건의 메타 (없으면 모두 null) */
  rfq_id: number | null;
  rfq_date: string | null;
  supplier_id: number | null;
  supplier_name: string | null;
  rfq_created_at: string | null;
  rfq_notes: string | null;
  /** 이 rfq 에 달린 supplier_quote 개수 */
  quote_count: number;
  /** derived 상태 — pending(rfq 없음) / sent(rfq 있고 quote 없음) / quoted(quote 있음) */
  status: RfqQueueStatus;
  /** option_part + quantity 모두 있어야 발송 가능. UI 발송 버튼 활성 조건. */
  can_send: boolean;
}

export interface RfqQueueResponse {
  date: string;
  summary: { pending: number; sent: number; quoted: number; total: number };
  items: RfqQueueItem[];
}

// 파일 로컬 스키마 — category(LeadCategory)/supplier(SupplierRow)는 미이관 도메인 타입이라
// z.custom(해당 필드만 통과)으로 두고, 해당 도메인 이관 시 정식 스키마로 교체.
const RfqQueueResponseSchema: z.ZodType<RfqQueueResponse> = z.object({
  date:    z.string(),
  summary: z.object({ pending: z.number(), sent: z.number(), quoted: z.number(), total: z.number() }),
  items:   z.array(z.object({
    lead_item_id:     z.number(),
    lead_id:          z.number(),
    category:         z.custom<LeadCategory | null>(() => true),
    option_part:      z.string().nullable(),
    item_description: z.string().nullable(),
    quantity:         z.number().nullable(),
    needs_rfq:        z.boolean(),
    item_created_at:  z.string(),
    customer_id:      z.number(),
    customer_name:    z.string().nullable(),
    rfq_id:           z.number().nullable(),
    rfq_date:         z.string().nullable(),
    supplier_id:      z.number().nullable(),
    supplier_name:    z.string().nullable(),
    rfq_created_at:   z.string().nullable(),
    rfq_notes:        z.string().nullable(),
    quote_count:      z.number(),
    status:           z.enum(['pending', 'sent', 'quoted']),
    can_send:         z.boolean(),
  })),
});

/** GET /api/admin/rfq/queue?date=YYYY-MM-DD&category=...&status=... */
export async function fetchRfqQueue(params: {
  date: string;
  category?: LeadCategory | null;
  status?: RfqQueueStatus | null;
}): Promise<RfqQueueResponse> {
  const sp = new URLSearchParams({ date: params.date });
  if (params.category) sp.set('category', params.category);
  if (params.status) sp.set('status', params.status);
  return getJson<RfqQueueResponse>(`/api/admin/rfq/queue?${sp.toString()}`, { schema: RfqQueueResponseSchema });
}

export interface MarkRfqSentInput {
  lead_item_ids: number[];
  supplier_id: number;
  rfq_date: string; // 'YYYY-MM-DD'
  inquiry_source?: '해외' | '국내' | '수출';
  notes?: string | null;
}

export interface MarkRfqSentResponse {
  inserted_count: number;
  rfqs: Record<string, unknown>[];
  supplier: SupplierRow;
}

const MarkRfqSentResponseSchema: z.ZodType<MarkRfqSentResponse> = z.object({
  inserted_count: z.number(),
  rfqs:           z.array(z.record(z.string(), z.unknown())),
  supplier:       z.custom<SupplierRow>(() => true),   // partner 도메인 이관 시 정식 스키마로
});

/** POST /api/admin/rfq/queue/mark-sent — 외부 발송 사실 기록 (rfq row 일괄 INSERT) */
export async function markRfqSent(input: MarkRfqSentInput): Promise<MarkRfqSentResponse> {
  return postJson<MarkRfqSentResponse>('/api/admin/rfq/queue/mark-sent', input, MarkRfqSentResponseSchema);
}

// ─── Sourcing API (P9-UI Step 2) ─────────────────────────────────────
// 타입·태그 상수는 shared 정본 (인라인 중복 제거 — 응답도 zod 검증).

/** 영업 요청 / 소싱 메모 태그 옵션 (§4.8.5-6) — shared 정본 re-export */
export const SALES_REQUEST_TAGS = SHARED_SALES_REQUEST_TAGS;
export const SOURCING_MEMO_TAGS = SHARED_SOURCING_MEMO_TAGS;

export type SalesRequestTag = typeof SALES_REQUEST_TAGS[number];
export type SourcingMemoTag = typeof SOURCING_MEMO_TAGS[number];

/**
 * 화면 state 의 `string[]` → 서버가 받는 태그 리터럴 배열.
 *
 * 화면은 체크박스 dataset(문자열)에서 태그를 모으므로 타입이 string[] 이고,
 * 서버 스키마는 z.enum 이라 리터럴 유니온이다. 캐스팅으로 넘기면 오타나 옛
 * 태그가 그대로 나가 400 이 되므로, 이 경계에서 **모르는 값은 버린다**.
 */
export function toSalesTags(v: readonly string[]): SalesRequestTag[] {
  return v.filter((t): t is SalesRequestTag =>
    (SALES_REQUEST_TAGS as readonly string[]).includes(t));
}
export function toMemoTags(v: readonly string[]): SourcingMemoTag[] {
  return v.filter((t): t is SourcingMemoTag =>
    (SOURCING_MEMO_TAGS as readonly string[]).includes(t));
}

/**
 * 니즈 생성 입력.
 *
 * ★ 손으로 복제하지 않는다 — shared 스키마에서 유도한다.
 *   예전엔 이 자리에 interface 를 직접 적어두어, shared 에 필드를 추가해도
 *   UI 는 모른 채 컴파일 에러만 났다(2026-07-31 source_deal_item_id).
 *   태그 상수만 shared 를 쓰고 타입은 복제돼 있던 상태였다.
 */
export type NeedCreateInput = SharedNeedCreateInput;

/** POST /api/admin/need — 니즈 생성 (딜 연결 선택 + 태그).
 *   응답은 생성된 원시 need 레코드(id/deal_id 숫자, 파생필드 없음) — 리스트 enriched 행이
 *   아니므로 NeedCreateResponseSchema(최소 검증)로 받는다. 호출측은 반환값 미사용, 재조회함. */
export async function createNeed(input: NeedCreateInput): Promise<NeedCreateResponse> {
  return postJson<NeedCreateResponse>('/api/admin/need', input, NeedCreateResponseSchema);
}

/**
 * POST /api/admin/need/from-deal-items — 딜 품목 선택 → 니즈 생성.
 *
 * 컨디션 투영(서버 구성품은 부모 서버의 NEW→F/S, USED→REF)·수량 × 서버 대수·
 * (파트넘버+컨디션) 병합·need_origin 링크를 **서버가** 한 트랜잭션에서 한다.
 * 화면은 선택한 deal_item id 만 보낸다 — 규칙이 화면에 있으면 LLM 도구 경로와
 * 갈라지고, need_origin 은 니즈 INSERT 와 같은 트랜잭션에서만 정확히 쓸 수 있다.
 */
// z.object 는 기본이 strip 이라 서버가 필드를 더 보내도 통과한다.
//   호출측은 created.length 만 쓰므로 최소 검증만 한다.
const NeedsFromItemsResponseSchema = z.object({
  created:      z.array(z.object({ id: z.number(), need_no: z.string() })),
  source_count: z.number(),
});
export type NeedsFromItemsResponse = z.infer<typeof NeedsFromItemsResponseSchema>;

export async function createNeedsFromDealItems(
  dealId:      string | number,
  dealItemIds: readonly string[],
): Promise<NeedsFromItemsResponse> {
  return postJson<NeedsFromItemsResponse>(
    '/api/admin/need/from-deal-items',
    { deal_id: Number(dealId), deal_item_ids: dealItemIds },
    NeedsFromItemsResponseSchema,
  );
}

/** DELETE /api/admin/need/:id — 니즈 소프트 삭제 (RFQ 이력 보존) */
export async function deleteNeed(id: string | number): Promise<void> {
  const res = await loadingFetch(
    `${API_BASE}/api/admin/need/${encodeURIComponent(String(id))}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  if (!res.ok) {
    throw await httpError(res);
  }
}

/** PATCH /api/admin/need/:id/tags — 영업요청/소싱메모 태그 수정 */
/**
 * 영업요청/소싱메모 태그 저장.
 *
 * 반환값을 쓰지 않는다(두 호출부 모두 로컬 상태를 먼저 갱신하고 이 호출은
 * 서버 반영만 한다). 그래서 응답도 검증하지 않는다.
 *
 * 예전엔 `SourcingNeedRowSchema` 로 검증했는데 **백엔드는 `Need` 를 돌려준다**.
 * SourcingNeedRow 는 소싱 목록 전용이라 deal_name·customer_name·rfq_count·
 * origins 같은 조인 필드를 요구하고, Need 에는 그게 없다. 그래서 저장은
 * 성공하는데 매번 검증에서 걸려 '태그 저장 실패' alert 이 떴다.
 *
 * 쓰지 않는 값을 억지로 맞추느라 백엔드 반환 타입을 바꾸는 건 과하다 —
 * 검증 대상에서 뺀다. 실패는 HTTP 상태로 이미 잡힌다.
 */
export async function updateNeedTags(
  needId: string | number,
  patch:  { sales_request_tags?: string[]; sourcing_memo_tags?: string[] },
): Promise<void> {
  await patchJson<unknown>(
    `/api/admin/need/${encodeURIComponent(String(needId))}/tags`,
    patch,
  );
}

/** PATCH /api/admin/need/:id — 니즈 요청 수량/컨디션 수정 */
export async function updateNeed(
  needId: string | number,
  input:  { quantity?: number; requested_condition?: string | null },
): Promise<void> {
  await patchJson<unknown>(
    `/api/admin/need/${encodeURIComponent(String(needId))}`,
    input,
  );
}

/** PATCH /api/admin/need/:id/purchase-request — 구매(매입) 요청 토글 (마이그119) */
export async function setNeedPurchaseRequest(
  needId:    string | number,
  requested: boolean,
): Promise<{ id: number; purchase_requested: boolean }> {
  return patchJson<{ id: number; purchase_requested: boolean }>(
    `/api/admin/need/${encodeURIComponent(String(needId))}/purchase-request`,
    { requested },
    NeedPurchaseRequestResponseSchema,
  );
}

/**
 * GET /api/admin/deal/:id/sourcing — need 평탄 리스트.
 * 딜 상세 페이지의 소싱 섹션이 사용.
 */
export async function fetchDealSourcing(
  id: string | number,
  opts: { signal?: AbortSignal } = {},
): Promise<DealSourcingResponse> {
  return getJson<DealSourcingResponse>(
    `/api/admin/deal/${encodeURIComponent(String(id))}/sourcing`,
    { ...opts, schema: DealSourcingResponseSchema },
  );
}

// SourcingListNeed — 관문(schemas.ts) z.infer 정본 (위 re-export 참조).

/** POST /api/admin/need/group-sort — 그룹 이동/순서 (group_key 에 sort_at 부여). */
export async function setGroupSort(groupKey: string, sortAt: string): Promise<void> {
  const res = await loadingFetch(`${API_BASE}/api/admin/need/group-sort`, {
    method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ group_key: groupKey, sort_at: sortAt }),
  });
  if (!res.ok) throw new Error(`이동 실패 (HTTP ${res.status})`);
}

/** 한 날짜 클러스터 일괄 재배치 — group_key 또는 need_id + sort_at. */
export type SortBatchItem = { group_key?: string; need_id?: string; sort_at: string };
export async function setSortBatch(items: SortBatchItem[]): Promise<void> {
  const res = await loadingFetch(`${API_BASE}/api/admin/need/sort-batch`, {
    method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ items }),
  });
  if (!res.ok) throw new Error(`이동 실패 (HTTP ${res.status})`);
}

/** POST /api/admin/need/:id/group — 드래그 묶기 (source=:id → target 그룹). */
export async function mergeNeedGroup(sourceId: string | number, targetId: string | number): Promise<void> {
  const res = await loadingFetch(`${API_BASE}/api/admin/need/${encodeURIComponent(String(sourceId))}/group`, {
    method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ target_id: Number(targetId) }),
  });
  if (!res.ok) throw new Error(`묶기 실패 (HTTP ${res.status})`);
}

/** POST /api/admin/need/:id/ungroup — 그룹 해제 (scope: 'self'|'all'). */
export async function ungroupNeed(id: string | number, scope: 'self' | 'all'): Promise<void> {
  const res = await loadingFetch(`${API_BASE}/api/admin/need/${encodeURIComponent(String(id))}/ungroup`, {
    method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ scope }),
  });
  if (!res.ok) throw new Error(`그룹 해제 실패 (HTTP ${res.status})`);
}

// 매입처 지정 모달용 SupplierPick 은 schemas.ts 의 z.infer 가 정본이다.
//   여기 있던 interface 복제본은 같은 이름의 import 와 충돌해 ts2440 을 냈다
//   (NeedCreateInput 과 같은 함정 — 타입을 손으로 베끼지 않는다).
export async function fetchActiveSuppliers(): Promise<SupplierPick[]> {
  // 실적 집계 포함 전용 엔드포인트 (가격수신 DESC 정렬).
  // 기존 손 정규화(Number/String 방어)는 스키마의 z.coerce가 대신한다.
  const r = await getJson<{ suppliers: SupplierPick[] }>(
    '/api/admin/rfq/sourcing-suppliers',
    { schema: SourcingSuppliersResponseSchema },
  );
  return r.suppliers;
}

// SyncClusterResponse — 관문 z.infer 정본 (위 re-export 참조).
/** PUT /api/admin/rfq/cluster — 클러스터 매입처 "집합" 동기화(지정 모달 저장). */
export async function syncClusterSuppliers(
  needIds:     Array<number | string>,
  supplierIds: Array<number | string>,
): Promise<SyncClusterResponse> {
  const res = await loadingFetch(`${API_BASE}/api/admin/rfq/cluster`, {
    method: 'PUT', headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      need_ids:     needIds.map(Number),
      supplier_ids: supplierIds.map(Number),
    }),
  });
  if (!res.ok) throw new Error(`매입처 저장 실패 (HTTP ${res.status})`);
  return parseResponse(await res.json(), SyncClusterResponseSchema, 'PUT /api/admin/rfq/cluster');
}

/** 가격 응답 popover 저장 — PATCH /api/admin/rfq/:id (단건 응답 수정). */
export interface RfqResponsePatch {
  status?:            string;
  price_amount?:      number | null;
  price_currency?:    string | null;
  sourced_condition?: string | null;
  responded_quantity?: number | null;
  alternative_part?:  string | null;
  rfq_memo_tags?:     string[];
  ajj_memo?:          string | null;
  request_note?:      string | null;
}
export async function updateRfqResponse(id: string | number, patch: RfqResponsePatch): Promise<void> {
  const res = await loadingFetch(`${API_BASE}/api/admin/rfq/${encodeURIComponent(String(id))}`, {
    method: 'PATCH', headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`가격 저장 실패 (HTTP ${res.status})`);
}

// SourcingCursor/SourcingListResponse — 관문 z.infer 정본 (위 re-export 참조).
/** GET /api/admin/need — 전사 활성 니즈 목록(키셋 페이지네이션). */
export async function fetchSourcingList(
  opts: { search?: string; cursor?: SourcingCursor | null; limit?: number; signal?: AbortSignal } = {},
): Promise<SourcingListResponse> {
  const p = new URLSearchParams();
  const s = opts.search?.trim();
  if (s)            p.set('search', s);
  if (opts.limit)   p.set('limit', String(opts.limit));
  if (opts.cursor) { p.set('cursorAt', opts.cursor.at); p.set('cursorId', opts.cursor.id); }
  const qs = p.toString();
  return getJson<SourcingListResponse>(`/api/admin/need${qs ? `?${qs}` : ''}`, { signal: opts.signal, schema: SourcingListResponseSchema });
}

// NeedRfq*/RfqGrouped*/RfqBatchFromDealItems* — shared z.infer 정본 (위 re-export 참조).
// 특히 RfqGroupedRow.deal_id/no/name nullable, NeedRfqsResponse.need.need_no·deal_id nullable은
// 이번 검증 작업에서 실측으로 shared를 교정한 항목(0712).

/** GET /api/admin/rfq — 전역 RFQ 리스트. (group_code × supplier) 그룹. */
export async function fetchRfqListGrouped(
  opts: { signal?: AbortSignal } = {},
): Promise<RfqListGroupedResponse> {
  return getJson<RfqListGroupedResponse>('/api/admin/rfq', { ...opts, schema: RfqListGroupedResponseSchema });
}

// ─── RFQ 요청하기 (P9-UI Step 2b) ────────────────────────────────────

/**
 * POST /api/admin/deal/:id/rfq-batch — 사용자가 품목 체크 후 "RFQ 요청하기" 클릭.
 *
 * 각 deal_item 마다 백엔드가:
 *   1) need 생성 (deal_item snapshot)
 *   2) auto-recommend 모드로 batch 생성
 *
 * 중복 허용 v1 — 같은 deal_item 두 번 요청하면 need 두 개.
 */
export async function postRfqBatchFromDealItems(
  dealId: string | number,
  body: { deal_item_ids: number[]; request_note?: string | null },
): Promise<RfqBatchFromDealItemsResponse> {
  return postJson<RfqBatchFromDealItemsResponse>(
    `/api/admin/deal/${encodeURIComponent(String(dealId))}/rfq-batch`,
    body,
    RfqBatchFromDealItemsResponseSchema,
  );
}

/**
 * GET /api/admin/need/:id/rfqs — need 단건 + 모든 batch + supplier 응답.
 * NeedRfqsView (/need/:id/rfqs) 가 사용.
 */
export async function fetchNeedRfqs(
  id: string | number,
  opts: { signal?: AbortSignal } = {},
): Promise<NeedRfqsResponse> {
  return getJson<NeedRfqsResponse>(
    `/api/admin/need/${encodeURIComponent(String(id))}/rfqs`,
    { ...opts, schema: NeedRfqsResponseSchema },
  );
}
